import { KeyDateInfo } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CollapsibleKeyDatesSection/CollapsibleKeyDatesSection.types"
import { CalendarEvent } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/AddToCalendar/AddToCalendar"
import { Election, DeadlineInfo, RegistrationInfo, VotingInfo } from "QuorumGrassroots/services/grassrootsCampaign"

export const getStateFromElection = (election: Election): string => {
    const state = election.ocd_id.split("/").pop()?.split(":").pop()
    return state.toUpperCase() || ""
}

/**
 * Format a date string (YYYY-MM-DD) to a human-readable format
 * Example: "2024-11-05" -> "Nov 5, 2024"
 */
export const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return ""

    try {
        const date = new Date(dateStr)
        return Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        }).format(date)
    } catch {
        return dateStr
    }
}

/**
 * Create a KeyDateInfo object from a DeadlineInfo
 */
const createKeyDateFromDeadline = (
    deadline: DeadlineInfo | null,
    description: string,
    url?: string | null,
): KeyDateInfo | null => {
    if (!deadline?.date) return null

    return {
        date: formatDate(deadline.date),
        description: deadline.postmarked_or_received
            ? `${description} (${deadline.postmarked_or_received})`
            : description,
        url: url || undefined,
    }
}

/**
 * Extract registration deadline dates from election data
 */
export const extractRegistrationDates = (registration: RegistrationInfo | null): KeyDateInfo[] => {
    if (!registration) return []

    const dates: KeyDateInfo[] = []

    // Online registration deadline
    const onlineDate = createKeyDateFromDeadline(
        registration.online?.deadline || null,
        "Online",
        registration.online?.url,
    )
    if (onlineDate) dates.push(onlineDate)

    // In-person registration deadline
    const inPersonDate = createKeyDateFromDeadline(
        registration.in_person?.deadline || null,
        "In-person",
        registration.explainer_url,
    )
    if (inPersonDate) dates.push(inPersonDate)

    // By mail registration deadline
    const byMailDate = createKeyDateFromDeadline(
        registration.by_mail?.deadline || null,
        "By mail",
        registration.explainer_url,
    )
    if (byMailDate) dates.push(byMailDate)

    return dates
}

/**
 * Extract absentee ballot request dates from election data
 */
export const extractAbsenteeBallotDates = (voting: VotingInfo | null): KeyDateInfo[] => {
    if (!voting?.by_mail?.deadline) return []

    const dates: KeyDateInfo[] = []
    const deadline = voting.by_mail.deadline

    // Ballot request deadline
    const requestDate = createKeyDateFromDeadline(
        deadline.ballot_request || null,
        "Request deadline",
        voting.by_mail.explainer_url,
    )
    if (requestDate) dates.push(requestDate)

    // Return by mail deadline
    const returnByMailDate = createKeyDateFromDeadline(
        deadline.return_by_mail || null,
        "Return by mail",
        voting.by_mail.explainer_url,
    )
    if (returnByMailDate) dates.push(returnByMailDate)

    // Return in person deadline
    const returnInPersonDate = createKeyDateFromDeadline(
        deadline.return_in_person || null,
        "Return in-person",
        voting.by_mail.explainer_url,
    )
    if (returnInPersonDate) dates.push(returnInPersonDate)

    return dates
}

/**
 * Extract early voting dates from election data
 */
export const extractEarlyVotingDates = (voting: VotingInfo | null): KeyDateInfo[] => {
    if (!voting?.early) return []

    const dates: KeyDateInfo[] = []
    const early = voting.early

    // Early voting start date
    if (early.start_date) {
        dates.push({
            date: formatDate(early.start_date),
            description: "Early voting starts",
            url: early.url || undefined,
        })
    }

    // Early voting end date
    if (early.end_date) {
        dates.push({
            date: formatDate(early.end_date),
            description: "Early voting ends",
            url: early.url || undefined,
        })
    }

    return dates
}

/**
 * Create a calendar event from election data
 */
export const createCalendarEvent = (election: Election): CalendarEvent => {
    const electionType = election.election_type.replace(/_/g, " ")
    const title = `${electionType} Election`

    return {
        title,
        description: `${title} - Make your voice heard!`,
        start: `${election.date}T08:00:00`,
        end: `${election.date}T20:00:00`,
        timezone: "America/New_York",
    }
}
